import React, { useState, useEffect } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { Button } from "../components/ui/button";
import LoginForm from "../components/LoginForm";
import { db } from "../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

// ─── Data Arrays ────────────────────────────────────────────────────────────

const CAPABILITIES = [
  {
    title: "Monitor",
    tagline: "Real-Time Grid & Plant Visibility",
    color: "blue",
    features: [
      "UK National Grid live generation and demand",
      "Grid imbalance analysis (24h balance)",
      "Interactive UK power plants map",
      "Storage projects tracking",
      "Live Energy & Climate Wire news feed",
    ],
  },
  {
    title: "Forecast",
    tagline: "ML-Powered Production Intelligence",
    color: "emerald",
    features: [
      "7-day production forecasts (LightGBM)",
      "Probabilistic ranges (P10/P50/P90)",
      "Weather-adjusted baselines",
      "Multi-source data: NASA POWER, Open-Meteo",
      "Historical comparison and accuracy tracking",
    ],
  },
  {
    title: "Protect",
    tagline: "Weather Risk & Portfolio Analytics",
    color: "amber",
    features: [
      "17+ weather indices per plant",
      "High-risk alerts: Storm, Soiling, Frost, Icing",
      "Soiling calendar (solar-specific)",
      "Wind performance dashboards",
      "Portfolio-level risk reports and revenue-at-risk",
    ],
  },
  {
    title: "Trade",
    tagline: "Derivatives, Commodities & Market Intelligence",
    color: "purple",
    features: [
      "Weather derivatives pricing (HDD/CDD/CAT/Wind/Solar)",
      "Monte Carlo simulation and Burn Analysis",
      "15+ commodity prices with supply chain risk",
      "Generation vs price correlation (Elexon)",
      "AI news intelligence with trading signals",
    ],
  },
];

const FEATURE_TABS = [
  {
    label: "Grid Dashboard",
    title: "Real-Time UK Grid Intelligence",
    desc: "Monitor generation mix, demand balance, and system prices from Elexon BMRS. Track wind, solar, gas, nuclear, and interconnector flows in real-time.",
    mockup: "grid",
  },
  {
    label: "Weather Risk",
    title: "17+ Weather Indices Per Plant",
    desc: "Storm severity, soiling risk, frost index, wind power density, and more. 7-day historical data plus 7-day forecasts with critical/high/moderate/low thresholds.",
    mockup: "risk",
  },
  {
    label: "Production Forecast",
    title: "ML-Powered 7-Day Forecasts",
    desc: "LightGBM quantile regression delivers P10/P50/P90 confidence bands. Separate models optimised for solar and wind assets with 30+ weather features.",
    mockup: "forecast",
  },
  {
    label: "Portfolio",
    title: "Multi-Asset Portfolio Analytics",
    desc: "Aggregate capacity, risk scores, and revenue-at-risk across your entire fleet. Regional analysis identifies geographic risk hotspots and diversification gaps.",
    mockup: "portfolio",
  },
  {
    label: "Commodities",
    title: "Supply Chain & Cost Intelligence",
    desc: "Track copper, lithium, rare earths, carbon prices, and more. 6 material cost indices (SMCI, WTCI, BSCI) with supply chain risk assessment and LCOE sensitivity.",
    mockup: "commodities",
  },
];

const PERSONAS = [
  { title: "Asset Managers", desc: "Optimise plant performance with production forecasts and portfolio-level risk reports. Know exactly where your revenue is at risk.", features: ["Production forecasting", "Portfolio risk reports", "Revenue-at-risk analysis", "Weather alerts"] },
  { title: "Energy Traders", desc: "Price weather derivatives, track commodity correlations, and get AI-powered trading signals from news intelligence.", features: ["Weather derivatives pricing", "Commodity tracking", "News trading signals", "Price-weather correlation"] },
  { title: "Grid Operators", desc: "Monitor real-time generation mix, demand balance, and grid imbalance. Track all UK power plants and storage projects.", features: ["Real-time grid dashboard", "Imbalance analysis", "Power plants map", "Generation mix"] },
  { title: "Renewable Developers", desc: "Assess site potential with weather indices, LCOE sensitivity analysis, and long-term climate data from NASA POWER.", features: ["Weather indices", "LCOE sensitivity", "Commodity cost indices", "Climate data analysis"] },
];

const DATA_SOURCES = [
  { name: "NASA POWER", desc: "Daily solar irradiance, temperature, wind speed, and 17+ climate parameters" },
  { name: "Open-Meteo", desc: "Weather forecasts and historical backfill with no delay" },
  { name: "Elexon BMRS", desc: "UK grid generation, demand, system prices, and interconnector flows" },
  { name: "FRED", desc: "Commodity prices from the Federal Reserve Economic Data" },
  { name: "Yahoo Finance", desc: "Real-time precious metals and energy commodity prices" },
  { name: "NewsAPI", desc: "Energy and climate news from configurable global sources" },
  { name: "GDELT", desc: "Global event data for broad energy and geopolitical monitoring" },
];

const HERO_STATS = [
  { value: 500, suffix: "+", label: "UK Plants Tracked" },
  { value: 17, suffix: "+", label: "Weather Indices" },
  { value: 7, suffix: "-Day", label: "ML Forecasts" },
  { value: 15, suffix: "+", label: "Commodities" },
];

const colorMap = {
  blue: { border: "border-blue-500", text: "text-blue-600", bg: "bg-blue-500/10", dot: "bg-blue-500" },
  emerald: { border: "border-emerald-500", text: "text-emerald-600", bg: "bg-emerald-500/10", dot: "bg-emerald-500" },
  amber: { border: "border-amber-500", text: "text-amber-600", bg: "bg-amber-500/10", dot: "bg-amber-500" },
  purple: { border: "border-purple-500", text: "text-purple-600", bg: "bg-purple-500/10", dot: "bg-purple-500" },
};

// ─── Animated Counter Component ─────────────────────────────────────────────

function AnimatedCounter({ value, suffix = "", label, dark = false }) {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    let start = 0;
    const duration = 1500;
    const increment = value / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= value) {
        setCount(value);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [isInView, value]);

  return (
    <div ref={ref} className="text-center">
      <div className={`text-3xl md:text-4xl font-bold ${dark ? "text-white" : "text-gray-900"}`}>
        {count}{suffix}
      </div>
      <div className={`text-sm mt-1 ${dark ? "text-gray-400" : "text-gray-500"}`}>{label}</div>
    </div>
  );
}

// ─── Dashboard Mockup Components ────────────────────────────────────────────

function MockChart({ color = "#3b82f6" }) {
  const id = `grad-${color.replace('#','')}`;
  return (
    <svg viewBox="0 0 200 60" className="w-full h-16" preserveAspectRatio="none">
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d="M0,45 Q25,35 50,38 T100,25 T150,30 T200,15" fill="none" stroke={color} strokeWidth="2" />
      <path d="M0,45 Q25,35 50,38 T100,25 T150,30 T200,15 L200,60 L0,60 Z" fill={`url(#${id})`} />
    </svg>
  );
}

function HeroDashboardMockup() {
  return (
    <motion.div
      initial={{ opacity: 0, x: 40 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 1, delay: 0.3 }}
      className="relative hidden lg:block"
    >
      <div className="absolute -inset-4 bg-blue-500/10 rounded-3xl blur-3xl animate-pulse-slow" />
      <div className="relative rounded-xl border border-gray-700 bg-gray-900 shadow-2xl overflow-hidden">
        {/* Title bar */}
        <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-800 border-b border-gray-700">
          <div className="w-3 h-3 rounded-full bg-red-500/80" />
          <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
          <div className="w-3 h-3 rounded-full bg-green-500/80" />
          <span className="ml-2 text-xs text-gray-400">PowerOptimise Dashboard</span>
        </div>
        {/* Content */}
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            {[
              { label: "Generation", val: "12.4 GW", color: "text-emerald-400" },
              { label: "Demand", val: "28.1 GW", color: "text-blue-400" },
              { label: "Wind Risk", val: "Low", color: "text-amber-400" },
            ].map((s) => (
              <div key={s.label} className="bg-gray-800 rounded-lg p-2.5 border border-gray-700">
                <div className="text-[10px] text-gray-500 uppercase tracking-wider">{s.label}</div>
                <div className={`text-sm font-semibold ${s.color}`}>{s.val}</div>
              </div>
            ))}
          </div>
          <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
            <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">7-Day Production Forecast</div>
            <MockChart color="#22c55e" />
          </div>
          <div className="space-y-1.5">
            {[
              { text: "Storm alert — 3 wind plants affected", severity: "HIGH", color: "bg-red-500/20 text-red-400" },
              { text: "Soiling risk rising — 2 solar plants", severity: "MED", color: "bg-amber-500/20 text-amber-400" },
            ].map((a) => (
              <div key={a.text} className="flex items-center justify-between bg-gray-800 rounded-lg px-3 py-2 border border-gray-700">
                <span className="text-[11px] text-gray-300">{a.text}</span>
                <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${a.color}`}>{a.severity}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ─── Feature Tab Mockups (kept dark for contrast against light backgrounds) ─

function TabMockup({ type }) {
  if (type === "grid") {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-4 gap-2">
          {["Wind 35%", "Gas 28%", "Nuclear 18%", "Solar 12%"].map((f) => (
            <div key={f} className="bg-gray-800 rounded-lg p-3 text-center border border-gray-700">
              <div className="text-xs text-gray-400">{f.split(" ")[0]}</div>
              <div className="text-lg font-bold text-white">{f.split(" ")[1]}</div>
            </div>
          ))}
        </div>
        <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
          <MockChart color="#3b82f6" />
        </div>
      </div>
    );
  }
  if (type === "risk") {
    return (
      <div className="space-y-2">
        {[
          { idx: "Storm Severity", val: "0.72", level: "HIGH", color: "text-red-400 bg-red-500/20" },
          { idx: "Soiling Risk", val: "0.45", level: "MED", color: "text-amber-400 bg-amber-500/20" },
          { idx: "Frost Risk", val: "0.18", level: "LOW", color: "text-emerald-400 bg-emerald-500/20" },
          { idx: "Wind Power Density", val: "485 W/m²", level: "Class 4", color: "text-blue-400 bg-blue-500/20" },
        ].map((r) => (
          <div key={r.idx} className="flex items-center justify-between bg-gray-800 rounded-lg px-4 py-3 border border-gray-700">
            <span className="text-sm text-gray-300">{r.idx}</span>
            <div className="flex items-center gap-3">
              <span className="text-sm font-mono text-white">{r.val}</span>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${r.color}`}>{r.level}</span>
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (type === "forecast") {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-2">
          {[
            { label: "P10 (Low)", val: "8.2 MW", color: "text-red-400" },
            { label: "P50 (Median)", val: "12.4 MW", color: "text-blue-400" },
            { label: "P90 (High)", val: "15.1 MW", color: "text-emerald-400" },
          ].map((p) => (
            <div key={p.label} className="bg-gray-800 rounded-lg p-3 text-center border border-gray-700">
              <div className="text-[10px] text-gray-500">{p.label}</div>
              <div className={`text-lg font-bold ${p.color}`}>{p.val}</div>
            </div>
          ))}
        </div>
        <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
          <MockChart color="#22c55e" />
        </div>
      </div>
    );
  }
  if (type === "portfolio") {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          {[
            { label: "Total Capacity", val: "245 MW" },
            { label: "Plants", val: "12" },
            { label: "Risk Score", val: "0.34" },
            { label: "Revenue at Risk", val: "£42K" },
          ].map((s) => (
            <div key={s.label} className="bg-gray-800 rounded-lg p-3 border border-gray-700">
              <div className="text-[10px] text-gray-500 uppercase">{s.label}</div>
              <div className="text-lg font-bold text-white">{s.val}</div>
            </div>
          ))}
        </div>
        <div className="flex gap-1 h-4 rounded-full overflow-hidden">
          <div className="bg-emerald-500 w-[45%]" title="Wind" />
          <div className="bg-amber-500 w-[35%]" title="Solar" />
          <div className="bg-blue-500 w-[20%]" title="Gas" />
        </div>
        <div className="flex gap-4 text-[10px] text-gray-400">
          <span><span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mr-1" />Wind 45%</span>
          <span><span className="inline-block w-2 h-2 rounded-full bg-amber-500 mr-1" />Solar 35%</span>
          <span><span className="inline-block w-2 h-2 rounded-full bg-blue-500 mr-1" />Gas 20%</span>
        </div>
      </div>
    );
  }
  if (type === "commodities") {
    return (
      <div className="space-y-2">
        {[
          { name: "Copper", price: "$8,942/t", chg: "+2.3%", up: true },
          { name: "Lithium", price: "$12,450/t", chg: "-1.8%", up: false },
          { name: "EU ETS Carbon", price: "€68.20/t", chg: "+0.7%", up: true },
          { name: "UK Natural Gas", price: "82.4p/th", chg: "-3.1%", up: false },
        ].map((c) => (
          <div key={c.name} className="flex items-center justify-between bg-gray-800 rounded-lg px-4 py-3 border border-gray-700">
            <span className="text-sm text-gray-300">{c.name}</span>
            <div className="flex items-center gap-3">
              <span className="text-sm font-mono text-white">{c.price}</span>
              <span className={`text-xs font-semibold ${c.up ? "text-emerald-400" : "text-red-400"}`}>{c.chg}</span>
            </div>
          </div>
        ))}
      </div>
    );
  }
  return null;
}

// ─── Fade-in wrapper ────────────────────────────────────────────────────────

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

function FadeIn({ children, className = "", delay = 0 }) {
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, delay }}
      variants={fadeInUp}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ─── Main Homepage Component ────────────────────────────────────────────────

export default function Homepage() {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  // Contact form state
  const [formName, setFormName] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formMessage, setFormMessage] = useState("");
  const [formRole, setFormRole] = useState("");
  const [formStatus, setFormStatus] = useState(null);

  // Scroll listener for navbar
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToSection = (id) => {
    setMobileMenuOpen(false);
    const section = document.getElementById(id);
    section?.scrollIntoView({ behavior: "smooth" });
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setFormStatus("sending");
    try {
      await addDoc(collection(db, "contactMessages"), {
        name: formName,
        email: formEmail,
        message: formMessage,
        role: formRole,
        createdAt: serverTimestamp(),
      });
      setFormStatus("success");
      setFormName("");
      setFormEmail("");
      setFormMessage("");
      setFormRole("");
    } catch (error) {
      console.error("Error submitting message:", error);
      setFormStatus("error");
    }
  };

  const navLinks = [
    { label: "Platform", id: "platform" },
    { label: "Features", id: "features" },
    { label: "Data Sources", id: "data-sources" },
    { label: "Contact", id: "contact" },
  ];

  return (
    <div className="font-sans text-gray-900 bg-white">

      {/* ─── 1. Navbar ────────────────────────────────────────────────── */}
      <nav className={`fixed top-0 z-50 w-full px-4 md:px-6 py-3 flex justify-between items-center transition-all duration-300 ${scrolled ? "bg-white/90 backdrop-blur-md border-b border-gray-200 shadow-sm" : "bg-transparent"}`}>
        <a href="/" className="flex items-center space-x-3">
          <img src="/images/poweroptimise-logo.png" alt="PowerOptimise Logo" className="h-8 sm:h-10" />
          <span className={`text-lg font-bold hidden sm:inline ${scrolled ? "text-gray-900" : "text-white"}`}>PowerOptimise</span>
        </a>
        {/* Desktop nav */}
        <div className="hidden md:flex items-center space-x-6">
          {navLinks.map((l) => (
            <button key={l.id} onClick={() => scrollToSection(l.id)} className={`text-sm transition ${scrolled ? "text-gray-600 hover:text-gray-900" : "text-gray-300 hover:text-white"}`}>{l.label}</button>
          ))}
          <button onClick={() => setShowLoginModal(true)} className={`text-sm transition ${scrolled ? "text-gray-500 hover:text-gray-900" : "text-gray-400 hover:text-white"}`}>Login</button>
          <Button size="sm" onClick={() => scrollToSection("contact")}>Book a Demo</Button>
        </div>
        {/* Mobile hamburger */}
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className={`md:hidden p-1 ${scrolled ? "text-gray-900" : "text-white"}`}>
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {mobileMenuOpen
              ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            }
          </svg>
        </button>
      </nav>

      {/* Mobile menu overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-white/95 backdrop-blur-md flex flex-col items-center justify-center space-y-6"
          >
            {navLinks.map((l) => (
              <button key={l.id} onClick={() => scrollToSection(l.id)} className="text-xl text-gray-700 hover:text-gray-900">{l.label}</button>
            ))}
            <button onClick={() => { setMobileMenuOpen(false); setShowLoginModal(true); }} className="text-xl text-gray-500 hover:text-gray-900">Login</button>
            <Button onClick={() => scrollToSection("contact")}>Book a Demo</Button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── 2. Hero (dark block — high contrast) ─────────────────────── */}
      <section className="min-h-screen pt-24 pb-16 px-4 md:px-6 flex items-center relative overflow-hidden bg-gradient-to-b from-gray-900 via-gray-800 to-gray-700 text-white">
        {/* Background glow */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="max-w-7xl mx-auto w-full grid lg:grid-cols-2 gap-12 items-center">
          {/* Left */}
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="space-y-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight text-white">
              Forecast, Protect and Trade Your{" "}
              <span className="text-gray-100">UK Renewable Energy Portfolio</span>
            </h1>
            <p className="text-lg text-gray-300 max-w-xl">
              Production forecasting, portfolio risk analytics, weather derivatives, and commodity tracking — powered by 7 live data sources and ML models trained on UK solar and wind assets.
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <Button size="lg" onClick={() => scrollToSection("contact")}>Book a Demo</Button>
              <Button size="lg" variant="outline-white" onClick={() => scrollToSection("platform")}>Explore the Platform</Button>
            </div>
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-6 border-t border-gray-600 mt-6">
              {HERO_STATS.map((s) => (
                <AnimatedCounter key={s.label} value={s.value} suffix={s.suffix} label={s.label} dark />
              ))}
            </div>
          </motion.div>
          {/* Right - Dashboard mockup */}
          <HeroDashboardMockup />
        </div>
      </section>

      {/* ─── Gradient transition: dark → light gray ──────────────────── */}
      <div className="h-24 bg-gradient-to-b from-gray-700 to-gray-100" />

      {/* ─── 3. Trust Bar ─────────────────────────────────────────────── */}
      <section className="py-8 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <p className="text-xs text-gray-500 uppercase tracking-widest text-center mb-4">Integrated with authoritative data sources</p>
          <div className="flex flex-wrap justify-center gap-3 md:gap-4">
            {DATA_SOURCES.map((s) => (
              <span key={s.name} className="px-3 py-1.5 text-xs text-gray-600 bg-white border border-gray-200 rounded-full whitespace-nowrap shadow-sm">{s.name}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Gradient transition: light gray → white → light gray ────── */}
      <div className="h-16 bg-gradient-to-b from-gray-100 to-gray-50" />

      {/* ─── 5. Core Capabilities (4 Pillars) ─────────────────────────── */}
      <section id="platform" className="py-20 md:py-28 px-4 md:px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <FadeIn className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Monitor. Forecast. Protect. Trade.</h2>
            <p className="text-gray-500 mt-4 max-w-2xl mx-auto">Four pillars of intelligence that cover the full lifecycle of renewable energy asset management.</p>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {CAPABILITIES.map((cap, i) => {
              const colors = colorMap[cap.color];
              return (
                <FadeIn key={cap.title} delay={i * 0.1}>
                  <div className="p-6 rounded-2xl border border-gray-200 bg-white h-full hover:shadow-md transition-shadow">
                    <h3 className={`text-lg font-semibold ${colors.text} mb-1`}>{cap.title}</h3>
                    <p className="text-sm text-gray-500 mb-4">{cap.tagline}</p>
                    <ul className="space-y-1.5">
                      {cap.features.map((f) => (
                        <li key={f} className="text-xs text-gray-500 flex items-center gap-2">
                          <svg className={`w-3.5 h-3.5 ${colors.text} flex-shrink-0`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── Gradient transition: light gray → dark gray ─────────────── */}
      <div className="h-24 bg-gradient-to-b from-gray-100 to-gray-800" />

      {/* ─── 6. Feature Deep-Dives (Tabbed — dark block for contrast) ── */}
      <section id="features" className="py-20 md:py-28 px-4 md:px-6 bg-gray-800 text-white">
        <div className="max-w-7xl mx-auto">
          <FadeIn className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-white">Built for Energy Professionals</h2>
            <p className="text-gray-400 mt-4 max-w-2xl mx-auto">Explore the tools that power smarter decisions across the UK energy sector.</p>
          </FadeIn>
          <div className="grid lg:grid-cols-5 gap-6">
            {/* Tab buttons */}
            <div className="lg:col-span-2 flex lg:flex-col gap-2 overflow-x-auto no-scrollbar pb-2 lg:pb-0">
              {FEATURE_TABS.map((tab, i) => (
                <button
                  key={tab.label}
                  onClick={() => setActiveTab(i)}
                  className={`text-left px-4 py-3 rounded-xl text-sm font-medium transition whitespace-nowrap flex-shrink-0 ${
                    activeTab === i
                      ? "bg-blue-600/20 text-blue-400 border border-blue-500/30"
                      : "text-gray-400 hover:text-white hover:bg-gray-700/50 border border-transparent"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            {/* Tab content */}
            <div className="lg:col-span-3">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  className="bg-gray-900 rounded-2xl border border-gray-700 p-6"
                >
                  <h3 className="text-xl font-semibold mb-2 text-white">{FEATURE_TABS[activeTab].title}</h3>
                  <p className="text-sm text-gray-400 mb-5">{FEATURE_TABS[activeTab].desc}</p>
                  <TabMockup type={FEATURE_TABS[activeTab].mockup} />
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Gradient transition: dark gray → light gray ─────────────── */}
      <div className="h-24 bg-gradient-to-b from-gray-800 to-gray-100" />

      {/* ─── 7. Who It's For (Personas) ───────────────────────────────── */}
      <section className="py-20 md:py-28 px-4 md:px-6 bg-gray-100">
        <div className="max-w-7xl mx-auto">
          <FadeIn className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Built for Every Role in Energy</h2>
            <p className="text-gray-500 mt-4 max-w-2xl mx-auto">Whether you manage assets, trade energy, operate grids, or develop renewables — PowerOptimise has you covered.</p>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {PERSONAS.map((p, i) => (
              <FadeIn key={p.title} delay={i * 0.1}>
                <div className="p-6 rounded-2xl border border-gray-200 bg-white h-full hover:shadow-md transition-shadow">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{p.title}</h3>
                  <p className="text-sm text-gray-500 mb-4">{p.desc}</p>
                  <ul className="space-y-1.5">
                    {p.features.map((f) => (
                      <li key={f} className="text-xs text-gray-500 flex items-center gap-2">
                        <svg className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Gradient transition: light gray → white ─────────────────── */}
      <div className="h-16 bg-gradient-to-b from-gray-100 to-white" />

      {/* ─── 8. Data Sources & Integrations ───────────────────────────── */}
      <section id="data-sources" className="py-20 md:py-28 px-4 md:px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <FadeIn className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Built on Authoritative Data Sources</h2>
            <p className="text-gray-500 mt-4 max-w-2xl mx-auto">Seven integrated data sources provide the foundation for reliable forecasts, risk analytics, and market intelligence.</p>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {DATA_SOURCES.map((s, i) => (
              <FadeIn key={s.name} delay={i * 0.06}>
                <div className="p-5 rounded-xl border border-gray-200 bg-gray-50 hover:shadow-sm transition-shadow">
                  <h4 className="text-sm font-semibold text-gray-900 mb-1">{s.name}</h4>
                  <p className="text-xs text-gray-500">{s.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Gradient transition: white → dark gray ──────────────────── */}
      <div className="h-24 bg-gradient-to-b from-white to-gray-800" />

      {/* ─── 9. Results / Stats (dark block for contrast) ─────────────── */}
      <section className="py-20 md:py-28 px-4 md:px-6 bg-gray-800">
        <div className="max-w-5xl mx-auto">
          <FadeIn className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-white">Purpose-Built for UK Renewable Energy</h2>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { value: 500, suffix: "+", label: "UK Power Plants", sub: "Tracked in real-time" },
              { value: 7, suffix: "", label: "Data Sources", sub: "Integrated and cross-referenced" },
              { value: 17, suffix: "+", label: "Risk Indices", sub: "Per plant, per day" },
            ].map((s, i) => (
              <FadeIn key={s.label} delay={i * 0.1}>
                <div className="text-center p-8 rounded-2xl border border-gray-700 bg-gray-900">
                  <AnimatedCounter value={s.value} suffix={s.suffix} label="" dark />
                  <div className="text-lg font-semibold text-white mt-2">{s.label}</div>
                  <div className="text-sm text-gray-400 mt-1">{s.sub}</div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Gradient transition: dark gray → white ──────────────────── */}
      <div className="h-24 bg-gradient-to-b from-gray-800 to-white" />

      {/* ─── 10. CTA + Contact Form ───────────────────────────────────── */}
      <section id="contact" className="py-20 md:py-28 px-4 md:px-6 bg-white">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-start">
          {/* Left - CTA */}
          <FadeIn>
            <div className="space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Start Making Smarter Energy Decisions</h2>
              <p className="text-gray-500">Get in touch to learn how PowerOptimise can help your team monitor, forecast, and manage weather risk across your renewable energy portfolio.</p>
            </div>
          </FadeIn>
          {/* Right - Form */}
          <FadeIn delay={0.2}>
            <form onSubmit={handleContactSubmit} className="space-y-4 p-6 rounded-2xl border border-gray-200 bg-gray-50">
              <div>
                <label htmlFor="name" className="block text-sm text-gray-600 mb-1">Name</label>
                <input type="text" id="name" className="w-full px-4 py-2.5 rounded-lg bg-white border border-gray-300 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition" value={formName} onChange={(e) => setFormName(e.target.value)} required />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm text-gray-600 mb-1">Email</label>
                <input type="email" id="email" className="w-full px-4 py-2.5 rounded-lg bg-white border border-gray-300 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition" value={formEmail} onChange={(e) => setFormEmail(e.target.value)} required />
              </div>
              <div>
                <label htmlFor="role" className="block text-sm text-gray-600 mb-1">Role / Interest</label>
                <select id="role" className="w-full px-4 py-2.5 rounded-lg bg-white border border-gray-300 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition" value={formRole} onChange={(e) => setFormRole(e.target.value)}>
                  <option value="">Select your role...</option>
                  <option value="asset-manager">Asset Manager</option>
                  <option value="trader">Energy Trader</option>
                  <option value="operator">Grid / Plant Operator</option>
                  <option value="developer">Renewable Developer</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label htmlFor="message" className="block text-sm text-gray-600 mb-1">Message</label>
                <textarea id="message" rows="4" className="w-full px-4 py-2.5 rounded-lg bg-white border border-gray-300 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition resize-none" value={formMessage} onChange={(e) => setFormMessage(e.target.value)} required />
              </div>
              <Button type="submit" size="lg" className="w-full">
                {formStatus === "sending" ? "Sending..." : "Send Message"}
              </Button>
              {formStatus === "success" && (
                <p className="text-emerald-600 text-sm text-center">Message sent successfully! We'll be in touch soon.</p>
              )}
              {formStatus === "error" && (
                <p className="text-red-600 text-sm text-center">Something went wrong. Please try again.</p>
              )}
            </form>
          </FadeIn>
        </div>
      </section>

      {/* ─── 11. Footer (dark) ────────────────────────────────────────── */}
      <footer className="py-12 px-4 md:px-6 border-t border-gray-200 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center space-x-2 mb-3">
              <img src="/images/poweroptimise-logo.png" alt="PowerOptimise" className="h-8" />
              <span className="font-bold text-white">PowerOptimise</span>
            </div>
            <p className="text-xs text-gray-400">AI-powered climate and energy analytics for UK renewable energy.</p>
          </div>
          {/* Product */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-3">Product</h4>
            <ul className="space-y-2 text-xs text-gray-400">
              <li><button onClick={() => scrollToSection("platform")} className="hover:text-white transition">Grid Dashboard</button></li>
              <li><button onClick={() => scrollToSection("features")} className="hover:text-white transition">Weather Risk</button></li>
              <li><button onClick={() => scrollToSection("features")} className="hover:text-white transition">Forecasting</button></li>
              <li><button onClick={() => scrollToSection("features")} className="hover:text-white transition">Portfolio Analytics</button></li>
            </ul>
          </div>
          {/* Company */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-3">Company</h4>
            <ul className="space-y-2 text-xs text-gray-400">
              <li><button onClick={() => scrollToSection("contact")} className="hover:text-white transition">Contact</button></li>
              <li><span className="text-gray-400 cursor-default">Privacy Policy</span></li>
              <li><span className="text-gray-400 cursor-default">Terms of Service</span></li>
            </ul>
          </div>
          {/* Connect */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-3">Connect</h4>
            <ul className="space-y-2 text-xs text-gray-400">
              <li><a href="mailto:info@poweroptimise.ai" className="hover:text-white transition">info@poweroptimise.ai</a></li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-8 pt-6 border-t border-gray-700 text-center text-xs text-gray-500">
          &copy; {new Date().getFullYear()} PowerOptimise. All rights reserved.
        </div>
      </footer>

      {/* ─── Login Modal ──────────────────────────────────────────────── */}
      <AnimatePresence>
        {showLoginModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50"
            onClick={() => setShowLoginModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-md relative border border-gray-200"
              onClick={(e) => e.stopPropagation()}
            >
              <button onClick={() => setShowLoginModal(false)} className="absolute top-3 right-4 text-gray-400 hover:text-gray-900 text-xl">&times;</button>
              <h3 className="text-xl font-bold mb-4 text-center text-gray-900">Login</h3>
              <LoginForm />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
